import React, { useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import badges from "../../utils/badgeData";
import { useTranslation } from "react-i18next";
import Button from "../Button";
import { ModalHeader, Spacer } from "../MasterCss";
import html2pdf from "html2pdf.js";
import { Share } from "@capacitor/share";
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Device } from '@capacitor/device';

const Wrapper = styled.div`
  background-image: linear-gradient(#060606, #08345c);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100vw;
  padding: 2vh 2vw;
  box-sizing: border-box;
  overflow-x: hidden;
`;

// const Input = styled.input`
//   font-size: 2.5vh;
//   padding: 1.2vh 1vw;
//   margin: 2vh 0;
//   width: 60vw;
//   max-width: 400px;
// `;
const Input = styled.input`
  font-family: Bungee, sans-serif;
  font-size: 2.8vh;
  padding: 1.6vh 2vw;
  margin: 2vh 0;
  width: 60vw;
  max-width: 400px;
  border-radius: 1.5vh; /* rounded / pill shape */
  border: none;
  outline: none;
  color: #333;
  text-align: center;

  background: #ffffff; /* white background */
  box-shadow: 6px 6px 4px rgba(0, 0, 0, 0.25),
              inset -10px -10px 4px #f0f0f0,
              inset 10px 10px 4px #cccccc;

  &::placeholder {
    color: #888; /* subtle gray placeholder */
    text-transform: uppercase;
    text-shadow: 1px 1px 1px rgba(0,0,0,0.1);
  }

  &:focus {
    box-shadow: 0 0 10px rgba(244,162,97,0.7), inset -5px -5px 6px #eee, inset 5px 5px 6px #ccc;
  }

  @media (max-width: 480px) {
    font-size: 2.1vh;
    padding: 1vh 2vw;
  }
`;

const ErrorText = styled.p`
  color: #ff4d4f;
  font-size: 2vh;
  margin: 0;
  text-align: center;
`;

const CertificateBox = styled.div`
  border: 1.5vh solid #f4a261;
  padding: 3vh 3vw;
  text-align: center;
  background-color: #fff8e1;
  border-radius: 1vh;
  max-width: 90vw;

  img {
    width: 10vw;
    max-width: 80px;
    margin-bottom: 2vh;
  }

  h2 {
    font-size: 3vh;
    margin-bottom: 2vh;
  }

  p {
    font-size: 2vh;
    margin: 1vh 0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 3vw;
  flex-wrap: wrap;
  width: 90vw;
  padding: 2vh 5vw;

  @media (max-width: 480px) {
    gap: 5vw;
    padding: 2vh 2vw;
  }
`;

const CertificateScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(); 
  const { count = 0, badgeGiven = 0 } = location.state || {};
  const badge = badges[badgeGiven] || badges[0];

  const [name, setName] = useState("");
  const [error, setError] = useState("");

  const detectPlatform = async () => {
    const info = await Device.getInfo();
    console.log(info);
    // info.platform can be: 'ios', 'android', 'web'
    return info.platform;
  };
  
// Generate PDF and save/download
const handleGeneratePDF = () => {
  if (!name.trim()) {
    setError(t('certificate.error'));
    return;
  }
  setError("");

  const element = document.getElementById("certificateDiv");
  if (!element) return;

  const today = new Date();
  const humanDate = today.toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US', {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  // Update certificate content dynamically
  const nameEl = element.querySelector("h2");
  const dateEl = element.querySelector("#date");
  if (nameEl) nameEl.textContent = name;
  if (dateEl) dateEl.textContent = humanDate;

  // Create a wrapper that matches PDF dimensions
  const wrapper = document.createElement("div");
  wrapper.style.cssText = `
    width: 297mm; /* A4 landscape width */
    height: 210mm; /* A4 landscape height */
    display: flex;
    justify-content: center;
    align-items: center;
    background: linear-gradient(#060606, #08345c);
    position: fixed;
    top: 0;
    left: 0;
    z-index: 9999;
    overflow: hidden;
  `;

  // Clone and center the certificate
  const clone = element.cloneNode(true);
  clone.style.margin = "0";
  clone.style.maxWidth = "80%"; /* Keep certificate reasonable size */
  
  wrapper.appendChild(clone);
  document.body.appendChild(wrapper);

  html2pdf()
    .set({
      margin: 0,
      filename: `${name}-certificate.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: { 
        scale: 2,
        backgroundColor: "#060606",
        useCORS: true,
        width: 1123, /* A4 landscape width in pixels */
        height: 794, /* A4 landscape height in pixels */
        windowWidth: 1123,
        windowHeight: 794,
      },
      jsPDF: { 
        unit: "mm", 
        format: "a4", 
        orientation: "landscape" 
      },
    })
    .from(wrapper)
    .save()
    .then(() => {
      // Clean up
      document.body.removeChild(wrapper);
      console.log("PDF generated successfully");
    })
    .catch((err) => {
      console.error("PDF generation error:", err);
      if (wrapper.parentNode) {
        document.body.removeChild(wrapper);
      }
    });
};

  const handleCertificate = async () => {
  const platform = await detectPlatform();

  if (platform === 'web') {
    handleGeneratePDF(); // browser
  } else {
    handleSharePDF();    // mobile app
  }
};

  const handleSharePDF = async () => {
  const element = document.getElementById("certificateDiv");
  if (!element) return;

  try {
    // Generate PDF as blob
    const pdfBlob = await html2pdf()
      .set({ margin: 0.5, filename: `${name}-certificate.pdf` })
      .from(element)
      .outputPdf("blob");

    // Convert blob to base64
    const reader = new FileReader();
    reader.readAsDataURL(pdfBlob);
    reader.onloadend = async () => {
      const base64Data = (reader.result).split(',')[1];

      // Save PDF to filesystem
      const fileName = `${name}-certificate.pdf`;
      const savedFile = await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Documents,
      });

      // Share the file using Capacitor Share plugin
      await Share.share({
        title: t('certificate.shareTitle'),
        text: t('certificate.shareText'),
        url: savedFile.uri, 
        dialogTitle: t('certificate.shareDialog'),
      });
    };
  } catch (err) {
    console.error("Error sharing PDF:", err);
  }
};

  return (
    <Wrapper>
      <Spacer />
      <ModalHeader>{t('certificate.title')}</ModalHeader>
      <Input
        type="text"
        placeholder={t('certificate.namePlaceholder')}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      {error && <ErrorText>{error}</ErrorText>}
      {error && <Spacer />}
      <CertificateBox id="certificateDiv">
        <h2>{name || t('certificate.defaultName')}</h2>
        <img src={badge.src} alt="Badge" />
        <p>
          {t('certificate.successMessage', { count: count })}
        </p>
        <p>{t('certificate.issuedBy')} <strong>D.Waste</strong></p>
        <p>
          {t('certificate.date')} <strong id="date">{new Date().toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US')}</strong>
        </p>
      </CertificateBox>
      <Spacer />
      <ButtonContainer>
        {/* <Button primary label="Download Certificate" handleClick={handleGeneratePDF} /> */}
        <Button primary label={t('certificate.shareButton')} handleClick={handleCertificate} />
        <Button label={t('certificate.backButton')} handleClick={() => navigate(-1)} />
      </ButtonContainer>
      <Spacer />
      <Spacer />
    </Wrapper>
  );
};

export default CertificateScreen;
